/**
 * Device Health Integration Service (MVP, local-first, honest)
 * -----------------------------------------------------------------------------
 * Why this rewrite (2026-05-23):
 *   The previous implementation fabricated step data: it seeded a random
 *   baseline of 500–2,500 steps the first time the user "connected", then
 *   added 5–25 fake steps every 30 seconds via setInterval. That eroded user
 *   trust because the numbers looked real but weren't measured from any
 *   device. This rewrite removes every Math.random() / setInterval cheat and
 *   establishes a small, honest, local-first contract that real native
 *   bridges (Health Connect / Google Fit / Apple Health) can drop into later
 *   without touching the page UI.
 *
 * Design rules (do NOT violate):
 *   - Never generate or invent step counts. Steps come only from:
 *       (a) the user's manual entry, or
 *       (b) a real native health bridge (window.HealthConnect, etc.).
 *   - All state is persisted in localStorage under the `healthloop.steps.*`
 *     namespace so existing app behaviour and other components are not
 *     affected. Old `steps_YYYY-MM-DD` keys are migrated lazily on first read.
 *   - Status is a single, explicit enum (see STATUS) — no vague "syncing".
 *   - Offline awareness uses navigator.onLine + 'online'/'offline' events.
 *     We never block local tracking when offline; we just stop pretending the
 *     backend is reachable.
 *   - All public methods are synchronous-friendly where possible so the UI
 *     can render the cached value instantly on mount (no spinner flash).
 */

const PERMISSION_KEY = 'healthloop.steps.permission';
const PLATFORM_KEY = 'healthloop.steps.platform';
const STEPS_KEY_PREFIX = 'healthloop.steps.day.'; // healthloop.steps.day.YYYY-MM-DD
const LAST_SYNC_KEY = 'healthloop.steps.lastSync';
const PENDING_QUEUE_KEY = 'healthloop.steps.pendingQueue';
const LEGACY_PERMISSION_KEY = 'healthPermission';
const LEGACY_PLATFORM_KEY = 'healthPlatform';

/**
 * Single source of truth for connection/tracking state. Any new state the UI
 * needs should be added here — no inline string literals downstream.
 */
export const STATUS = Object.freeze({
  UNAVAILABLE: 'unavailable',         // platform/native bridge not present at all
  PERMISSION_NEEDED: 'permission_needed',
  PERMISSION_DENIED: 'permission_denied',
  CONNECTED_WAITING: 'connected_waiting', // permitted but no data has flowed yet
  CONNECTED_ACTIVE: 'connected_active',   // permitted and we have a value for today
  OFFLINE: 'offline',                  // device offline; local tracking continues
  SYNC_PAUSED: 'sync_paused',          // explicit pause (e.g. backend unreachable repeatedly)
});

function todayKey() {
  return new Date().toISOString().split('T')[0];
}

function safeReadInt(key) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  } catch (_) {
    return null;
  }
}

function safeWriteInt(key, value) {
  try { localStorage.setItem(key, String(Math.max(0, parseInt(value, 10) || 0))); } catch (_) {}
}

class DeviceHealthService {
  constructor() {
    this.platform = this.detectPlatform();
    this.lastSyncTime = this._readLastSync();
    this.syncPaused = false;
    this._migrateLegacyKeys();
  }

  // ---------- platform detection -----------------------------------------

  detectPlatform() {
    const ua = (navigator.userAgent || navigator.vendor || (window.opera || '')).toString();
    if (/android/i.test(ua)) return 'android';
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
    return 'web';
  }

  getPlatformName() {
    switch (this.platform) {
      case 'android': return 'Google Fit / Health Connect';
      case 'ios': return 'Apple Health';
      default: return 'Device Health';
    }
  }

  /**
   * Does the runtime expose any native bridge we can talk to? Web returns
   * `true` so we can still offer the manual-entry "connected" experience,
   * but the UI should make it clear it's a manual flow until a native bridge
   * actually appears.
   */
  isAvailable() {
    if (this.platform === 'android') return !!(window.HealthConnect || window.GoogleFit);
    if (this.platform === 'ios') return !!(window.AppleHealth || window.webkit?.messageHandlers?.health);
    return true; // web/MVP — manual-entry path still viable
  }

  hasNativeBridge() {
    if (this.platform === 'android') return !!(window.HealthConnect || window.GoogleFit);
    if (this.platform === 'ios') return !!(window.AppleHealth || window.webkit?.messageHandlers?.health);
    return false;
  }

  // ---------- permission --------------------------------------------------

  /**
   * Best-effort permission request. On platforms with a real native bridge
   * we'd call into it here; for the web MVP we use a simple confirm() so the
   * permission state remains explicitly user-driven (never granted silently).
   */
  async requestPermission() {
    try {
      const userChoice = window.confirm(
        `Allow HealthLoop to read your ${this.getPlatformName()} step data?\n\n` +
        `Your wellness data stays on your device. We never invent step counts — ` +
        `only values measured by your device or entered by you are stored.`
      );
      if (userChoice) {
        localStorage.setItem(PERMISSION_KEY, 'granted');
        localStorage.setItem(PLATFORM_KEY, this.platform);
        return { granted: true, platform: this.platform };
      }
      localStorage.setItem(PERMISSION_KEY, 'denied');
      return { granted: false, platform: this.platform };
    } catch (_) {
      return { granted: false, platform: this.platform };
    }
  }

  hasPermission() {
    try { return localStorage.getItem(PERMISSION_KEY) === 'granted'; } catch (_) { return false; }
  }

  permissionDenied() {
    try { return localStorage.getItem(PERMISSION_KEY) === 'denied'; } catch (_) { return false; }
  }

  // ---------- status ------------------------------------------------------

  /**
   * Returns the current explicit state. The UI maps each value to copy and
   * an icon — no ambiguity, no "Syncing..." that never resolves.
   */
  getStatus() {
    if (this.syncPaused) return STATUS.SYNC_PAUSED;
    if (typeof navigator !== 'undefined' && navigator.onLine === false && this.hasPermission()) {
      return STATUS.OFFLINE;
    }
    if (!this.hasPermission()) {
      if (this.permissionDenied()) return STATUS.PERMISSION_DENIED;
      if (!this.isAvailable()) return STATUS.UNAVAILABLE;
      return STATUS.PERMISSION_NEEDED;
    }
    const todayValue = safeReadInt(STEPS_KEY_PREFIX + todayKey());
    return todayValue && todayValue > 0 ? STATUS.CONNECTED_ACTIVE : STATUS.CONNECTED_WAITING;
  }

  getConnectionStatus() {
    return {
      connected: this.hasPermission(),
      platform: this.platform,
      platformName: this.getPlatformName(),
      lastSync: this.lastSyncTime,
      status: this.getStatus(),
      online: typeof navigator !== 'undefined' ? navigator.onLine !== false : true,
    };
  }

  // ---------- step data (LOCAL ONLY, never invented) ----------------------

  /**
   * Read today's cached step count instantly (synchronous). Returns 0 when
   * we have nothing recorded yet — the UI shows the empty-state guidance
   * rather than a spinner.
   */
  getCachedSteps(date) {
    const key = STEPS_KEY_PREFIX + (date || todayKey());
    return safeReadInt(key) || 0;
  }

  /**
   * Persist a manually-entered or natively-read step value for a date.
   * Returns the new value so callers can update local UI immediately.
   */
  setSteps(steps, date) {
    const key = STEPS_KEY_PREFIX + (date || todayKey());
    const safeValue = Math.max(0, parseInt(steps, 10) || 0);
    safeWriteInt(key, safeValue);
    this._writeLastSync(new Date());
    return safeValue;
  }

  /**
   * Add an increment (used by future native pedometer bridges, NOT by
   * fabricated timers). Kept here so we have a single place native
   * integrations will plug into.
   */
  addSteps(delta, date) {
    const current = this.getCachedSteps(date);
    return this.setSteps(current + (parseInt(delta, 10) || 0), date);
  }

  /**
   * Returns the latest known value plus metadata. Asynchronous-friendly so
   * we can later swap in a real bridge that needs to await native I/O,
   * without changing call sites.
   */
  async readSteps(date) {
    return {
      steps: this.getCachedSteps(date),
      lastSync: this.lastSyncTime,
      platform: this.platform,
      source: this.hasNativeBridge() ? 'native_bridge' : 'manual',
    };
  }

  // ---------- offline queue ----------------------------------------------

  /**
   * Queue an update we couldn't deliver to the backend yet (network offline,
   * auth missing, etc.). Idempotent on (date) — only the most recent value
   * per day is retained so the queue stays tiny.
   */
  queuePending(entry) {
    try {
      const raw = localStorage.getItem(PENDING_QUEUE_KEY);
      const queue = raw ? JSON.parse(raw) : [];
      const filtered = queue.filter((q) => q.date !== entry.date);
      filtered.push({ ...entry, queuedAt: new Date().toISOString() });
      localStorage.setItem(PENDING_QUEUE_KEY, JSON.stringify(filtered.slice(-20)));
    } catch (_) {}
  }

  getPending() {
    try {
      const raw = localStorage.getItem(PENDING_QUEUE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (_) { return []; }
  }

  clearPending() {
    try { localStorage.removeItem(PENDING_QUEUE_KEY); } catch (_) {}
  }

  // ---------- sync state --------------------------------------------------

  markSynced() {
    this._writeLastSync(new Date());
    this.syncPaused = false;
  }

  pauseSync() { this.syncPaused = true; }
  resumeSync() { this.syncPaused = false; }

  // ---------- lifecycle ---------------------------------------------------

  disconnect() {
    try {
      localStorage.removeItem(PERMISSION_KEY);
      localStorage.removeItem(PLATFORM_KEY);
      // Intentionally keep day-by-day step counts so the user doesn't lose
      // their history when they revoke permission temporarily.
    } catch (_) {}
    return { success: true };
  }

  // ---------- internal ----------------------------------------------------

  _readLastSync() {
    try {
      const raw = localStorage.getItem(LAST_SYNC_KEY);
      return raw ? new Date(raw) : null;
    } catch (_) { return null; }
  }

  _writeLastSync(date) {
    try {
      this.lastSyncTime = date;
      localStorage.setItem(LAST_SYNC_KEY, date.toISOString());
    } catch (_) {}
  }

  /**
   * One-time, idempotent migration of the previous (fabricated-data) key
   * layout to the new namespace. We migrate the permission flag and the
   * legacy `steps_YYYY-MM-DD` key for today only — older fabricated days
   * are deliberately discarded to avoid carrying invented data forward.
   */
  _migrateLegacyKeys() {
    try {
      const legacyPerm = localStorage.getItem(LEGACY_PERMISSION_KEY);
      if (legacyPerm && !localStorage.getItem(PERMISSION_KEY)) {
        localStorage.setItem(PERMISSION_KEY, legacyPerm);
        localStorage.removeItem(LEGACY_PERMISSION_KEY);
      }
      const legacyPlatform = localStorage.getItem(LEGACY_PLATFORM_KEY);
      if (legacyPlatform && !localStorage.getItem(PLATFORM_KEY)) {
        localStorage.setItem(PLATFORM_KEY, legacyPlatform);
        localStorage.removeItem(LEGACY_PLATFORM_KEY);
      }
      // We intentionally do NOT migrate `steps_YYYY-MM-DD` legacy keys —
      // those were seeded by the old fabricated-data path and would carry
      // invented counts into the new trust model. Clear today's key only.
      try { localStorage.removeItem(`steps_${todayKey()}`); } catch (_) {}
    } catch (_) {}
  }
}

const deviceHealthService = new DeviceHealthService();
export default deviceHealthService;
